import React from "react";
import styled from "styled-components";
import { Page } from "@ui/components/page";
import { desktop, mobile } from "@ui/styles/utils";

type Block =
	| { kind: "paragraph"; text: string }
	| { kind: "quote"; text: string }
	| { kind: "list"; items: string[] }
	| { kind: "definitions"; items: { term: string; text: string }[] }
	| { kind: "contacts"; items: { label: string; value: string; href?: string }[] };

interface Section {
	title: string;
	blocks: Block[];
}

const sections: Section[] = [
	{
		title: "1. Préambule et objet",
		blocks: [
			{
				kind: "paragraph",
				text: "TooShare (ci-après « la Plateforme » ou « TooShare ») est un réseau social éducatif, ouvert et gratuit, se présentant comme le premier Opérateur Éducatif Digital, permettant à des formateurs, e-Teachers, écoles, instituts, universités, organisations et institutions (ci-après les « Formateurs ») de créer et animer des espaces d'apprentissage numériques appelés « Classrooms » ou « Shareroom », et à des apprenants de s'y inscrire pour suivre des contenus pédagogiques (cours, insights, carrousels, quiz), participer à des sessions en direct, consulter des supports (documents, PDF, calendriers), accéder à l'Eduspace et échanger au sein de la communauté.",
			},
			{
				kind: "paragraph",
				text: "Les présentes Conditions Générales d'Utilisation (ci-après les « CGU ») ont pour objet de définir les modalités et conditions dans lesquelles TooShare, (ci-après « l'Éditeur »), met la Plateforme à disposition des utilisateurs, ainsi que les droits et obligations des parties dans ce cadre.",
			},
			{
				kind: "paragraph",
				text: "Elles s'appliquent à toute utilisation de la Plateforme, que ce soit via le site web ou l'application mobile, par toute personne y accédant (ci-après l'« Utilisateur »), qu'elle agisse en qualité d'Apprenant, de Formateur ou de simple visiteur.",
			},
			{
				kind: "quote",
				text: "TooShare est un réseau social éducatif, ouvert et gratuit. C'est un mode d'acquisition de connaissances ouvert, dynamique et adapté aux besoins des utilisateurs reposant en grande partie sur les cours gratuits et/ou à des coûts accessibles afin de contribuer à l'accélération et à la transformation de nos économies. TooShare, un écosystème éducatif pensé, créé, et développé pour relever les nouveaux défis de l'éducation et de la formation pour tous en Afrique. Extend your world !",
			},
		],
	},
	{
		title: "2. Définitions",
		blocks: [
			{
				kind: "definitions",
				items: [
					{
						term: "Plateforme",
						text: "le site internet et/ou l'application TooShare, quel que soit le support d'accès.",
					},
					{
						term: "Utilisateur",
						text: "toute personne physique ou morale accédant à la Plateforme, inscrite ou non.",
					},
					{
						term: "Apprenant",
						text: "Utilisateur inscrit à un ou plusieurs Classrooms pour suivre des contenus pédagogiques.",
					},
					{
						term: "Formateur / Organisateur",
						text: "Utilisateur créant et administrant un Classroom, publiant des Contenus et pouvant percevoir une rétribution à ce titre.",
					},
					{
						term: "Classroom / Shareroom",
						text: "espace virtuel créé par un Formateur, regroupant des Contenus, des sessions en direct et des échanges avec les Apprenants inscrits.",
					},
					{
						term: "Contenu",
						text: "tout élément publié, téléversé ou partagé sur la Plateforme (texte, image, document, PDF, vidéo, message, avis, etc.), par l'Éditeur ou par un Utilisateur.",
					},
					{
						term: "Portefeuille (Wallet)",
						text: "compte virtuel associé au profil d'un Formateur, crédité des sommes issues de la monétisation de ses Classrooms et pouvant faire l'objet de retraits (cashout).",
					},
					{
						term: "Compte",
						text: "espace personnel créé par un Utilisateur lors de son inscription, protégé par des identifiants.",
					},
					{
						term: "Eduspace",
						text: "espace de contenus éducatifs (cours, insights, carrousels, quiz) ouvert à l'ensemble des Utilisateurs, avec outil de recherche intégré.",
					},
					{
						term: "e-Teacher",
						text: "Formateur utilisant les fonctionnalités numériques de la Plateforme (Live, LMS, Médiathèque) pour dispenser des cours ou du soutien en ligne.",
					},
					{
						term: "Compte Pro",
						text: "Compte à destination des professionnels, entrepreneurs et créateurs de contenus, donnant accès à des fonctionnalités étendues (LMS, monétisation) pour la création et la diffusion de contenus.",
					},
				],
			},
		],
	},
	{
		title: "3. Acceptation des CGU",
		blocks: [
			{
				kind: "paragraph",
				text: "L'accès et l'utilisation de la Plateforme impliquent l'acceptation pleine et entière des présentes CGU. Lors de son inscription, l'Utilisateur doit expressément cocher une case attestant qu'il a lu et accepte les CGU ; à défaut, l'inscription ne peut être finalisée et l'accès aux fonctionnalités réservées aux membres reste bloqué.",
			},
			{
				kind: "paragraph",
				text: "Cette acceptation vaut signature électronique et a, entre les parties, la même valeur probante qu'une signature manuscrite. Les CGU en vigueur sont celles accessibles sur la Plateforme au moment de la connexion de l'Utilisateur ; l'Utilisateur est invité à les consulter régulièrement.",
			},
		],
	},
	{
		title: "4. Accès à la Plateforme, inscription et compte utilisateur",
		blocks: [
			{
				kind: "paragraph",
				text: "L'accès à certaines fonctionnalités de la Plateforme (création ou suivi d'un Classroom, messagerie, wallet, etc.) nécessite la création d'un Compte. L'Utilisateur s'engage à fournir des informations exactes, à jour et complètes lors de son inscription, et à les maintenir à jour.",
			},
			{
				kind: "paragraph",
				text: "L'Utilisateur est seul responsable de la confidentialité de ses identifiants de connexion et de toute activité réalisée depuis son Compte. Il s'engage à informer immédiatement l'Éditeur de toute utilisation non autorisée de son Compte ou de toute atteinte à la sécurité de ses identifiants.",
			},
			{
				kind: "paragraph",
				text: "L'Éditeur se réserve le droit de refuser une inscription, ou de suspendre un Compte, en cas d'informations manifestement fausses, incomplètes ou frauduleuses, ou en cas de manquement aux présentes CGU.",
			},
		],
	},
	{
		title: "5. Description des services",
		blocks: [
			{ kind: "paragraph", text: "La Plateforme propose notamment :" },
			{
				kind: "list",
				items: [
					"la création et l'administration de Classrooms par les Formateurs (contenus, calendrier, sessions en direct) ;",
					"l'inscription des Apprenants à un ou plusieurs Classrooms et l'accès aux contenus associés (documents, PDF, vidéos) ;",
					"un tableau de bord permettant de suivre l'activité, les avis et les statistiques d'un Classroom ;",
					"des fonctionnalités de session en direct (Live) et de calendrier des événements ;",
					"un système d'avis et de retours des Apprenants sur les Classrooms suivis ;",
					"des fonctionnalités de monétisation et de gestion financière à destination des Formateurs (voir article 7) ;",
					"un réseau social éducatif avec profils, messagerie et géolocalisation des profils (retrouver un professeur, un e-Teacher ou un camarade à proximité) ;",
					"une Médiathèque et un Eduspace regroupant des contenus classés par catégorie et par tendance (cours, insights, carrousels, quiz) ;",
					"un LMS (système de gestion de l'apprentissage) permettant aux Formateurs de créer leurs propres contenus, le cas échéant assisté par des outils d'IA générative de contenus éducatifs ;",
					"un Compte Pro à destination des professionnels et créateurs de contenus ;",
					"une offre en marque blanche, permettant à un établissement, un groupe scolaire ou un État d'opérer la Plateforme sous sa propre bannière, dans les conditions d'un contrat spécifique conclu avec l'Éditeur.",
				],
			},
			{
				kind: "paragraph",
				text: "L'Éditeur se réserve le droit de faire évoluer, ajouter ou supprimer tout ou partie des fonctionnalités de la Plateforme, sans que cela ne puisse engager sa responsabilité, sous réserve d'en informer les Utilisateurs concernés dans un délai raisonnable lorsque cette évolution affecte substantiellement le service.",
			},
		],
	},
	{
		title: "6. Contenu publié par les utilisateurs et propriété intellectuelle",
		blocks: [
			{
				kind: "paragraph",
				text: "La structure générale de la Plateforme, ses textes, logos, marques, graphismes et logiciels sont la propriété exclusive de l'Éditeur ou de ses partenaires et sont protégés par le droit de la propriété intellectuelle. Toute reproduction, représentation ou exploitation non autorisée est interdite.",
			},
			{
				kind: "paragraph",
				text: "Chaque Utilisateur demeure titulaire des droits de propriété intellectuelle sur les Contenus qu'il publie. En publiant un Contenu, l'Utilisateur concède à l'Éditeur une licence non exclusive, gratuite et mondiale d'utilisation, de reproduction, d'adaptation et de représentation de ce Contenu, pour la seule durée de sa publication et dans la stricte mesure nécessaire au fonctionnement, à la promotion et à l'amélioration de la Plateforme.",
			},
			{
				kind: "paragraph",
				text: "L'Utilisateur garantit détenir tous les droits nécessaires sur les Contenus qu'il publie et s'engage à ne pas porter atteinte aux droits de tiers (droits d'auteur, droit à l'image, droit des marques, etc.). Tout Contenu jugé contraire aux présentes CGU, à la loi ou aux droits de tiers peut être signalé par un Utilisateur (notamment pour « violation des CGU de la plateforme ») et retiré par l'Éditeur à tout moment, sans préavis.",
			},
		],
	},
	{
		title: "7. Monétisation, paiements et portefeuille (Wallet)",
		blocks: [
			{
				kind: "paragraph",
				text: "Les Formateurs peuvent activer la monétisation d'un Classroom. Cette activation est subordonnée à l'acceptation préalable, expressée via une case à cocher dédiée, des règles et conditions de monétisation propres à chaque Classroom (barème, engagement de qualité, règles de la communauté), présentées lors de cette activation.",
			},
			{
				kind: "paragraph",
				text: "Les sommes issues de la monétisation sont créditées sur le Portefeuille (Wallet) associé au Compte du Formateur. Le Formateur peut suivre l'historique de ses transactions et demander le retrait (cashout) des sommes disponibles vers le moyen de paiement qu'il aura renseigné, sous réserve des délais de traitement et, le cas échéant, des frais applicables, précisés sur la Plateforme au moment de la demande.",
			},
			{
				kind: "paragraph",
				text: "L'Éditeur peut recourir à un ou plusieurs prestataires de services de paiement tiers pour le traitement des transactions et des retraits ; l'utilisation de ces services peut être soumise à des conditions complémentaires propres à ces prestataires. L'Éditeur se réserve le droit de suspendre ou de retenir temporairement une somme en cas de suspicion de fraude, d'activité anormale ou de non-respect des présentes CGU, le temps nécessaire aux vérifications utiles.",
			},
			{
				kind: "paragraph",
				text: "Toute somme monétisée demeure soumise aux obligations fiscales et sociales applicables au Formateur, qui reste seul responsable de leur déclaration auprès des administrations compétentes.",
			},
		],
	},
	{
		title: "8. Obligations et comportements interdits",
		blocks: [
			{
				kind: "paragraph",
				text: "L'Utilisateur s'engage à utiliser la Plateforme conformément à sa destination, dans le respect des lois en vigueur, des droits des tiers et des présentes CGU. Il est notamment interdit de :",
			},
			{
				kind: "list",
				items: [
					"publier des Contenus illicites, diffamatoires, injurieux, discriminatoires, violents ou portant atteinte à la dignité humaine ;",
					"usurper l'identité d'un tiers ou fournir de fausses informations lors de l'inscription ;",
					"porter atteinte aux droits de propriété intellectuelle ou aux données personnelles d'autrui ;",
					"contourner la Plateforme pour échapper aux règles de monétisation ou aux frais applicables ;",
					"perturber le fonctionnement de la Plateforme (virus, tentative d'intrusion, extraction massive de données) ;",
					"utiliser la Plateforme à des fins commerciales non autorisées par l'Éditeur.",
				],
			},
			{
				kind: "paragraph",
				text: "Tout manquement constaté peut entraîner, selon sa gravité, un avertissement, le retrait du Contenu concerné, la suspension ou la résiliation du Compte, sans préjudice de toute action judiciaire que l'Éditeur ou un tiers lésé jugerait utile d'engager.",
			},
		],
	},
	{
		title: "9. Protection des mineurs",
		blocks: [
			{
				kind: "paragraph",
				text: "L'inscription d'un mineur en tant qu'Apprenant est soumise à l'autorisation préalable de son représentant légal, qui demeure responsable de l'utilisation de la Plateforme par le mineur. L'Éditeur se réserve le droit de demander à tout moment une preuve de cette autorisation.",
			},
			{
				kind: "paragraph",
				text: "La création d'un Compte Formateur, l'activation de la monétisation et l'utilisation du Portefeuille sont réservées aux Utilisateurs majeurs et juridiquement capables de contracter.",
			},
		],
	},
	{
		title: "10. Responsabilité et garanties",
		blocks: [
			{
				kind: "paragraph",
				text: "L'Éditeur met en œuvre les moyens raisonnables pour assurer un accès continu et sécurisé à la Plateforme, sans pouvoir garantir une disponibilité permanente ni l'absence totale d'erreurs ou d'interruptions, notamment pour des raisons de maintenance, de force majeure ou de dysfonctionnement des réseaux de communication.",
			},
			{
				kind: "paragraph",
				text: "L'Éditeur n'est pas partie à la relation pédagogique entre Formateurs et Apprenants et n'exerce pas de contrôle éditorial préalable sur les Contenus publiés par les Utilisateurs. Il ne saurait être tenu responsable de la qualité, de l'exactitude ou de la licéité des Contenus publiés par un Utilisateur, ni des relations ou litiges pouvant naître entre Utilisateurs.",
			},
			{
				kind: "paragraph",
				text: "La responsabilité de l'Éditeur ne saurait être engagée en cas de dommage indirect, de perte de données, de perte d'exploitation ou de préjudice financier résultant de l'utilisation ou de l'impossibilité d'utiliser la Plateforme, sauf faute prouvée de sa part et dans les limites autorisées par la loi.",
			},
		],
	},
	{
		title: "11. Données personnelles",
		blocks: [
			{
				kind: "paragraph",
				text: "Dans le cadre de l'utilisation de la Plateforme, l'Éditeur collecte et traite des données à caractère personnel concernant les Utilisateurs. Les modalités de collecte, de traitement et de conservation de ces données, ainsi que les droits dont dispose l'Utilisateur (accès, rectification, effacement, opposition, portabilité) en application du Règlement Général sur la Protection des Données (RGPD) et de la loi Informatique et Libertés, sont décrites dans la Politique de confidentialité accessible sur la Plateforme, qui fait partie intégrante des présentes CGU.",
			},
			{
				kind: "paragraph",
				text: "En cas de contradiction entre les présentes CGU et la Politique de confidentialité sur un point relatif aux données personnelles, cette dernière prévaut.",
			},
		],
	},
	{
		title: "12. Durée, suspension et résiliation",
		blocks: [
			{
				kind: "paragraph",
				text: "Les présentes CGU s'appliquent pendant toute la durée d'utilisation de la Plateforme par l'Utilisateur. Ce dernier peut à tout moment supprimer son Compte depuis les paramètres de la Plateforme ou en contactant l'Éditeur.",
			},
			{
				kind: "paragraph",
				text: "L'Éditeur peut suspendre ou résilier, temporairement ou définitivement, l'accès d'un Utilisateur à tout ou partie de la Plateforme en cas de manquement aux présentes CGU, après mise en demeure restée infructueuse lorsque le manquement le permet, ou sans préavis en cas de manquement grave (fraude, contenu illicite, atteinte à la sécurité de la Plateforme).",
			},
			{
				kind: "paragraph",
				text: "La résiliation du Compte d'un Formateur reste sans effet sur les sommes déjà dues aux Apprenants ou déjà créditées sur son Portefeuille, qui demeurent régies par les règles de retrait applicables au moment de la résiliation.",
			},
		],
	},
	{
		title: "13. Modification des CGU",
		blocks: [
			{
				kind: "paragraph",
				text: "L'Éditeur se réserve le droit de modifier les présentes CGU à tout moment, notamment pour tenir compte des évolutions de la Plateforme ou de la réglementation applicable. Les Utilisateurs seront informés de toute modification substantielle par tout moyen approprié (notification sur la Plateforme, courriel) avant son entrée en vigueur.",
			},
			{
				kind: "paragraph",
				text: "La poursuite de l'utilisation de la Plateforme après l'entrée en vigueur des CGU modifiées vaut acceptation de celles-ci. Si l'Utilisateur refuse ces modifications, il lui appartient de cesser d'utiliser la Plateforme et, le cas échéant, de supprimer son Compte.",
			},
		],
	},
	{
		title: "14. Droit applicable et règlement des litiges",
		blocks: [
			{
				kind: "paragraph",
				text: "Les présentes CGU sont régies par le droit sénégalais, y compris les Actes uniformes OHADA applicables, sans préjudice des dispositions impératives de protection du consommateur applicables au lieu de résidence de l'Utilisateur.",
			},
			{
				kind: "paragraph",
				text: "En cas de litige, l'Utilisateur est invité à contacter en priorité le service client de l'Éditeur afin de rechercher une solution amiable. À défaut d'accord amiable, les tribunaux compétents de Dakar (Sénégal) seront seuls compétents, sous réserve des dispositions d'ordre public applicables au lieu de résidence de l'Utilisateur.",
			},
		],
	},
	{
		title: "15. Contact et mentions légales",
		blocks: [
			{
				kind: "contacts",
				items: [
					{
						label: "Éditeur",
						value: "TooShare, dont le siège est situé 32 Rue Jules Ferry, Dakar 17121, Sénégal.",
					},
					{ label: "Téléphone", value: "77 602 73 43", href: "tel:776027343" },
					{ label: "Email", value: "info@tooshare.com", href: "mailto:info@tooshare.com" },
					{
						label: "Horaires d'ouverture",
						value:
							"ferme à 17h00 (horaires indicatifs, susceptibles d'évolution ; se référer à la fiche établissement TooShare pour les horaires à jour).",
					},
					{
						label: "Contact",
						value:
							"pour toute question relative aux présentes CGU, l'Utilisateur peut contacter TooShare au 77 602 73 43, à l'adresse info@tooshare.com, ou via le formulaire de contact disponible sur la Plateforme.",
					},
				],
			},
		],
	},
];

export const CguPage: React.FC = () => {
	return (
		<Page>
			<Document>
				<Intro>
					<Kicker>TooShare</Kicker>
					<Title>Conditions Générales d'Utilisation</Title>
					<Effective>En vigueur au 2026-09-17</Effective>
				</Intro>
				{sections.map((section) => (
					<Article key={section.title}>
						<ArticleTitle>{section.title}</ArticleTitle>
						{section.blocks.map((block, index) => (
							<BlockView key={`${section.title}-${index}`} block={block} />
						))}
					</Article>
				))}
			</Document>
		</Page>
	);
};

const BlockView: React.FC<{ block: Block }> = ({ block }) => {
	switch (block.kind) {
		case "paragraph":
			return <Paragraph>{block.text}</Paragraph>;
		case "quote":
			return <Quote>« {block.text} »</Quote>;
		case "list":
			return (
				<List>
					{block.items.map((item) => (
						<li key={item}>{item}</li>
					))}
				</List>
			);
		case "definitions":
			return (
				<Definitions>
					{block.items.map((item) => (
						<div key={item.term}>
							<dt>{item.term}</dt>
							<dd>{item.text}</dd>
						</div>
					))}
				</Definitions>
			);
		case "contacts":
			return (
				<Contacts>
					{block.items.map((item) => (
						<div key={item.label}>
							<dt>{item.label}</dt>
							<dd>{item.href ? <a href={item.href}>{item.value}</a> : item.value}</dd>
						</div>
					))}
				</Contacts>
			);
	}
};

const Document = styled.article`
	max-width: 820px;
	margin: 0 auto;
	padding: 48px 56px 64px;
	background: ${({ theme }) => theme.colors.backgroundPrimary};
	border: 1px solid ${({ theme }) => theme.colors.lightergray};
	border-radius: 16px;
	color: ${({ theme }) => theme.colors.textPrimary};
	font-family: Sora, sans-serif;

	@media ${mobile} {
		padding: 28px 20px 40px;
		border-radius: 0;
		border-left: none;
		border-right: none;
	}
`;

const Intro = styled.header`
	padding-bottom: 28px;
	margin-bottom: 8px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
`;

const Kicker = styled.p`
	margin: 0 0 10px;
	color: ${({ theme }) => theme.colors.primary};
	font-size: 13px;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
`;

const Title = styled.h1`
	margin: 0 0 16px;
	font-size: 2rem;
	line-height: 1.2;
	font-weight: 700;
	letter-spacing: -0.4px;

	@media ${mobile} {
		font-size: 1.5rem;
	}
`;

const Effective = styled.p`
	display: inline-flex;
	margin: 0;
	padding: 6px 12px;
	border-radius: 999px;
	background: ${({ theme }) => theme.colors.primaryLight};
	color: ${({ theme }) => theme.colors.primaryTouch};
	font-size: 0.875rem;
	font-weight: 600;
`;

const Article = styled.section`
	padding: 28px 0;
	border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};

	&:last-child {
		border-bottom: none;
		padding-bottom: 0;
	}
`;

const ArticleTitle = styled.h2`
	margin: 0 0 14px;
	font-size: 1.25rem;
	line-height: 1.35;
	font-weight: 700;

	@media ${desktop} {
		font-size: 1.35rem;
	}
`;

const Paragraph = styled.p`
	margin: 0 0 14px;
	font-size: 0.975rem;
	line-height: 1.7;
	font-weight: 400;

	&:last-child {
		margin-bottom: 0;
	}
`;

const Quote = styled.blockquote`
	margin: 8px 0 0;
	padding: 16px 18px;
	border-left: 3px solid ${({ theme }) => theme.colors.primary};
	border-radius: 0 12px 12px 0;
	background: ${({ theme }) => theme.colors.primaryLight};
	color: ${({ theme }) => theme.colors.textPrimary};
	font-size: 0.95rem;
	line-height: 1.65;
	font-weight: 500;
`;

const List = styled.ul`
	margin: 0 0 14px;
	padding-left: 1.2rem;

	li {
		margin: 0 0 8px;
		font-size: 0.975rem;
		line-height: 1.65;
	}

	li::marker {
		color: ${({ theme }) => theme.colors.primary};
	}
`;

const Definitions = styled.dl`
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 14px;

	dt {
		font-weight: 700;
		font-size: 0.95rem;
	}

	dd {
		margin: 4px 0 0;
		color: ${({ theme }) => theme.colors.textSecondary};
		font-size: 0.95rem;
		line-height: 1.6;
		font-weight: 400;
	}
`;

const Contacts = styled.dl`
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 14px;

	dt {
		font-weight: 700;
		font-size: 0.85rem;
		color: ${({ theme }) => theme.colors.primary};
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	dd {
		margin: 4px 0 0;
		font-size: 0.975rem;
		line-height: 1.6;
		font-weight: 400;
	}

	a {
		color: ${({ theme }) => theme.colors.primary};
		font-weight: 600;
		text-decoration: underline;
	}
`;
